/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import { html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { ControlStateMixin } from '../../utils/control-state-mixin.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { decimalSeparator } from '../../utils/decimal-separator.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { FocusBlurMixin } from '../../utils/focus-blur-mixin.js';
import { KEYCODE } from '../../utils/keycode.js';
import { inputNumberStyles } from './sh-input-number-styles.js';
import './sh-numeric-keypad.js';

/**
 * @prop {String} label - Defines the label of the input. If the value is empty, it will be shown as a placeholder.
 * @prop {Number} value - Represents the current value of the input and only takes a number. Can be used for data binding and changes upon user input. Temporarily converts type number to type text for the value attribute when one of the buttons of keypad is pressed. Resets value to type number after keypad is closed. More robust way to get type number consistently would be to use getValue() method as mentioned below. Consequently, as a result , the attributes decimal-seperator and validation-pattern have been removed.
 * @prop {Number} step - Defines the size of the increment/decrement when the user presses the minus or plus step icons.
 * @prop {Boolean} disabled - If set, disabled styles will be applied
 * @prop {Boolean} readonly - If set to `true`, the field will be set to readonly, not allowing keyboard input and the step icons hidden.
 * @prop {Number} min - Represents the minimum value of the input. When defined, the value defaults to min value if user input exceeds the limit.
 * @prop {Number} max - Represents the maximum value of the input. When defined, the value defaults to max value if user input exceeds the limit.
 * @prop {Boolean} mandatory - If set to `true`, the input number displays styles for mandatory input.
 * @prop {Boolean} error - If set to `true`, the input number displays styles for invalid input.
 * @prop {Boolean} success - If set to `true`, the input number displays styles for valid input.
 * @prop {Boolean} safety - If set to `true`, the input number displays styles for safety-relevant input (yellow outline).
 * @prop {String} errorMessage - Displays a message when error property is set to true.
 * @attr {String} error-message - Displays a message when error property is set to true.
 * @prop {String} hint - Displays a message below the input box.
 * @prop {Boolean} condensed - If input number is inside a table, then the condensed format of input number is used to display information. This feature cannot be overriden.
 * @prop {Number} decimals - Defines a limit of decimal numbers allowed (e.g. if set to 2, 1.000 value will be rounded to 1.00).
 * @prop {Boolean} noClear - If set to `true`, the clear functionality is disabled.
 * @attr {Boolean} no-clear - If set to true, the clear functionality is disabled.
 * @prop {Boolean} noArrows - If set to `true`, the arrows will not be displayed.
 * @attr {Boolean} no-arrows - If set to true, the arrows will not be displayed.
 * @prop {Boolean} inputDisabled - If set to `true`, the input will not allow keyboard input.
 * @attr {Boolean} input-disabled - If set to true, the input will not allow keyboard input
 * @prop {Boolean} keypad - If set to `true`, the input will open a keypad on input focus. Now the keypad behaves like popover, flipping up when it reaches the end of screen. So the scroll-into-view and scrolling-parent-id attributes have been removed.
 * @prop {Boolean} keypadActive - Indicates whether the keypad is open or closed. Can also set it to true / false to force open/close the keypad.
 * @attr {Boolean} keypad-active - Indicates whether the keypad is open or closed. Can also set it to true / false to force open/close the keypad.
 * @prop {Boolean} keypadDisabled - If set to `true`, the keypad buttons will be disabled.
 * @attr {Boolean} keypad-disabled - If set to true, the keypad buttons will be disabled.
 * @prop {String} displayValue - *hide
 * @prop {String} role - *hide
 * @prop {Boolean} noBorder - If set to `true`, the bottom border will not be displayed.
 * @attr {Boolean} no-border - If set to true, the bottom border will not be displayed.
 * @prop {Boolean} noValidate - If set to `true`, the input number will not do the validations when the focus is moved out of the input. The validations will have to be externally handled by the developer.
 * @attr {Boolean} no-validate - If set to true, the input number will not do the validations when the focus is moved out of the input. The validations will have to be externally handled by the developer.
 * @prop {Boolean} europeanNumberConvention - This property is auto-set or cleared initially when the component is loaded. If the browser's language follows European number conventions, then this property is auto-set to true. Else it is auto-cleared. This property can also be forced set to true to force it to follow European number conventions. Also, this property can be forced set to false to follow English number conventions. This would be particularly useful when used in systems like Android where changing the browser language is not so easy.
 * @attr {Boolean} european-number-convention - This property is auto-set or cleared initially when the component is loaded. If the browser's language follows European number conventions, then this property is auto-set to true. Else it is auto-cleared. This property can also be forced set to true to force it to follow European number conventions. Also, this property can be forced set to false to follow English number conventions. This would be particularly useful when used in systems like Android where changing the browser language is not so easy.
 * @fires keypad-opened - Fired when the keypad is opened.
 * @fires keypad-closed - Fired when the keypad is closed.
 * @fires minusPressed - Fired when left facing icon is pressed.
 * @fires plusPressed - Fired when right facing icon is pressed.
 */

export class SHInputNumber extends ControlStateMixin(FocusBlurMixin(LitElement)) {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      value: { type: Number, reflect: true },
      step: { type: Number, reflect: true },
      readonly: { type: Boolean, reflect: true },
      min: { type: Number, reflect: true },
      max: { type: Number, reflect: true },
      mandatory: { type: Boolean, reflect: true },
      error: { type: Boolean, reflect: true },
      success: { type: Boolean, reflect: true },
      safety: { type: Boolean, reflect: true },
      hint: { type: String, reflect: true },
      condensed: { type: Boolean, reflect: true },
      decimals: { type: Number, reflect: true },
      keypad: { type: Boolean, reflect: true },
      /** @ignore */
      errorMessage: { type: String, reflect: true, attribute: 'error-message' },
      /** @ignore */
      noClear: { type: Boolean, reflect: true, attribute: 'no-clear' },
      /** @ignore */
      noArrows: { type: Boolean, reflect: true, attribute: 'no-arrows' },
      /** @ignore */
      inputDisabled: { type: Boolean, reflect: true, attribute: 'input-disabled' },
      /** @ignore */
      keypadActive: { type: Boolean, reflect: true, attribute: 'keypad-active' },
      /** @ignore */
      keypadDisabled: { type: Boolean, reflect: true, attribute: 'keypad-disabled' },
      /** @ignore */
      noBorder: { type: Boolean, reflect: true, attribute: 'no-border' },
      /** @ignore */
      noValidate: { type: Boolean, reflect: true, attribute: 'no-validate' },
      /** @ignore */
      europeanNumberConvention: {
        type: Boolean,
        reflect: true,
        attribute: 'european-number-convention',
      },
      /** @ignore */
      role: { type: String, reflect: true },
      /** @ignore */
      _displayValue: { type: String },
      /** @ignore */
      _decimalSeparator: { type: String },
      /** @ignore */
      _inputMode: { type: String },
      /** @ignore */
      _cursorPosition: { type: Number },
    };
  }

  static get styles() {
    return [inputNumberStyles, sharedStyles];
  }

  render() {
    return html`
      <div class="host-wrapper">
        <div class="content-wrapper">
          <input
            id="input"
            type="text"
            ?readonly="${this.readonly}"
            ?disabled="${this.disabled}"
            min="${this.min}"
            max="${this.max}"
            step="${this.step}"
            inputmode="${this._inputMode}"
            @click="${(e) => {
              this._storeCursorPosition(e.target);
            }}"
            @keydown="${(e) => {
              this._restrictOnlyNumberKeys(e);
              if (!this.inputDisabled) {
                this._closeKeypadIfBackTabbed(e);
              } else {
                e.preventDefault();
              }
            }}"
            @input="${(e) => this._updateDisplayValue(e)}"
            @focus="${(e) => this._inputFocus(e)}"
          />

          ${this.label
            ? html`
                <div class="input-number-label">
                  <div class="label-wrapper">${this.label}</div>
                  <span id="mandatory"> *</span>
                </div>
              `
            : ''}
          ${!this.noClear
            ? html`
                <sh-icon
                  button
                  size="s"
                  icon="cancel"
                  @click="${this._clearField}"
                  id="clear"
                  @focus="${(e) => {
                    e.target.onkeyup = (e) => {
                      this._clearValueIfEnterKeyPressed(e);
                    };
                  }}"
                ></sh-icon>
              `
            : ''}
          ${!this.noArrows
            ? html`
                <div class="icon-slot">
                  <sh-icon
                    button
                    icon="left-s"
                    id="minus"
                    ?disabled="${this.disabled}"
                    @keypress="${(e) => this._releaseMinusCounter(e)}"
                    @mousedown="${(e) => this._startMinusCounter(e)}"
                    @mouseup="${(e) => this._releaseMinusCounter(e)}"
                    @touchstart="${(e) => this._startMinusCounter(e)}"
                    @touchend="${(e) => this._releaseMinusCounter(e)}"
                  ></sh-icon>
                  <sh-icon
                    button
                    icon="right-s"
                    id="plus"
                    ?disabled="${this.disabled}"
                    @keypress="${(e) => this._releasePlusCounter(e)}"
                    @mousedown="${(e) => this._startPlusCounter(e)}"
                    @mouseup="${(e) => this._releasePlusCounter(e)}"
                    @touchstart="${(e) => this._startPlusCounter(e)}"
                    @touchend="${(e) => this._releasePlusCounter(e)}"
                  ></sh-icon>
                </div>
              `
            : ''}
        </div>

        <div class="icon-wrapper">
          <slot name="icon" id="icon"></slot>
          ${this.error
            ? html` <sh-icon
                icon="error"
                class="error-icon"
                size="s"
                ?disabled="${this.disabled}"
              ></sh-icon>`
            : ''}
          ${this.success
            ? html` <sh-icon
                icon="success"
                class="success-icon"
                size="s"
                ?disabled="${this.disabled}"
              ></sh-icon>`
            : ''}
        </div>

        ${this.keypad
          ? html`
              <sh-numeric-keypad
                @key-press="${(e) => {
                  this._updateInputBox(e, this.shadowRoot.querySelector('input'));
                }}"
                @closing="${() => {
                  this._closeKeypadIfTabbed();
                }}"
                .target=${this}
                .disabled=${this.keypadDisabled}
                .visible=${this.keypadActive}
                .condensed=${this.condensed}
                .decimals=${this.decimals}
                .europeanNumberConvention=${this.europeanNumberConvention}
              >
                <slot name="functions" slot="functions"></slot>
                <slot name="footer" slot="footer"></slot>
              </sh-numeric-keypad>
            `
          : ''}
      </div>
      ${this.hint
        ? html`
            <sh-text size="body-2" class="helper-text hint" color="secondary">${this.hint}</sh-text>
          `
        : ''}
      ${this.error
        ? html`
            <sh-text size="body-2" class="helper-text error-message" color="secondary"
              >${this.errorMessage}</sh-text
            >
          `
        : ''}
    `;
  }

  constructor() {
    super();
    this.label = 'label';
    this.step = 1;
    this.decimals = '';
    this._decimalSeparator = '.';
    this._inputMode = 'numeric';
    this._displayValue = '';
    this.boundMove = this._releasePlusCounter.bind(this);
    this.closeListener = this._closeInputKeyPad.bind(this);
    this._rAFCallBack = this._showKeypad.bind(this);
  }

  firstUpdated() {
    super.firstUpdated();
    if (!this.disabled && !this.readonly && !this.noClear) {
      this.shadowRoot.querySelector('#clear').setAttribute('tabindex', '0');
    }
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('value')) {
      this.valueChanged();
    }
    if (changedProperties.has('decimals')) {
      this._decimalsObserver();
    }
    if (changedProperties.has('inputDisabled')) {
      this._handleInputDisabledChange();
    }
    if (changedProperties.has('keypad')) {
      this._keypadObserver();
    }
    if (changedProperties.has('keypadActive')) {
      this._handleKeypadActive();
    }
    if (changedProperties.has('europeanNumberConvention')) {
      this._europeanNumberConventionObserver();
    }
    const listOfProperties = [
      'label',
      'value',
      'step',
      'readonly',
      'disabled',
      'min',
      'max',
      'mandatory',
      'error',
      'success',
      'safety',
      'hint',
      'condensed',
      'decimals',
      'keypad',
      'errorMessage',
      'noClear',
      'noArrows',
      'inputDisabled',
      'keypadActive',
      'keypadDisabled',
      'noBorder',
      'noValidate',
      'europeanNumberConvention',
      'role',
    ];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  valueChanged() {
    if (!this.value && this.value !== 0) {
      this.classList.add('empty');
      this._displayValue = '';
    } else {
      this.classList.remove('empty');
      if (this.value || this.value === 0) {
        const valueOfInput = this.value.toString();
        if (valueOfInput !== '' && valueOfInput.indexOf('.') > -1) {
          const decimalPlaces = valueOfInput.split('.')[1].length;
          this.shadowRoot.querySelector('#input').step = Math.pow(10, -decimalPlaces);
        }
        let numberSystem;
        if (this.europeanNumberConvention === undefined || this.europeanNumberConvention === null) {
          numberSystem = decimalSeparator === ',' ? 'European' : 'English';
        } else {
          numberSystem = this.europeanNumberConvention ? 'European' : 'English';
        }
        if (numberSystem === 'English') {
          this._displayValue = this.value.toString().split(',').join('.');
        } else {
          this._displayValue = this.value.toString().split('.').join(',');
        }
      }
    }
    this._displayValueChange();
  }

  connectedCallback() {
    super.connectedCallback();
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    }
    if (!this.value && this.value !== 0) {
      this.classList.add('empty');
    }

    document.body.addEventListener('mousemove', this.boundMove);
    this.addEventListener('blur', () => {
      this._validateOnBlurOnNormalInput();
    });

    this._checkMaxMin();
    this._renewStableValue(this.value);
    /**
     * If europeanNumberConvention property is not boolean,
     * then that means europeanNumberConvention property was never
     * set by the user. So it can be set by checking locale string.
     *
     * Otherwise, do not set the europeanNumberConvention property.
     */
    if (this.europeanNumberConvention === undefined || this.europeanNumberConvention === null) {
      this._setBrowserLanguageBasedEuropeanNumberConventionProperty();
    }
    if (!this.value && this.value !== 0) {
      this.classList.add('empty');
    }
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'textbox');
    }
    this.addEventListener('keydown', (e) => {
      this._closeKeypadIfTabbedOnAppropriateElement(e);
    });
  }

  getTabTarget() {
    return this.shadowRoot.querySelector('input');
  }

  getValue() {
    if (this.value !== undefined && this.value !== null) {
      const valueInNumber = parseFloat(this.value.toString(), 10);
      if (!isNaN(valueInNumber)) {
        return valueInNumber;
      } else {
        return '';
      }
    }
  }

  async _updateInputBox(e, inputBox) {
    const isEraseButton = e.detail.isEraseButton;
    /* temporarily changing the type of input to text to allow the appearance of dot */
    let buttonValue;
    if (!isEraseButton) {
      buttonValue = Number(e.detail.keyValueString.toString());
    }
    const buttonString = e.detail.keyValueString.toString();
    const valueString = this._displayValue ? this._displayValue.toString() : '';
    const isEitherNumberOrDecimalSeparator =
      (buttonString !== '' && buttonValue >= 0 && buttonValue <= 9) ||
      buttonString === '.' ||
      buttonString === ',';
    this._cursorPosition = inputBox.selectionStart;
    let tempDisplayValue;
    /* if button's value is either dot, comma or number, then insert value at cursor position */
    if (isEitherNumberOrDecimalSeparator) {
      tempDisplayValue =
        valueString.slice(0, this._cursorPosition) +
        buttonString +
        valueString.slice(this._cursorPosition);
      this.value = tempDisplayValue.toString().split(',').join('.');
      await inputBox.updateComplete;
      inputBox.setSelectionRange(this._cursorPosition + 1, this._cursorPosition + 1);
    }
    /* if button is erase button and cursor position is not at the beginning of the input value,
       then remove the previous character at the cursor position */
    if (isEraseButton && this._cursorPosition !== 0) {
      tempDisplayValue =
        valueString.slice(0, this._cursorPosition - 1) + valueString.slice(this._cursorPosition);
      this.value = tempDisplayValue.toString().split(',').join('.');
      await inputBox.updateComplete;
      inputBox.setSelectionRange(this._cursorPosition - 1, this._cursorPosition - 1);
    }
    this._cursorPosition = inputBox.selectionStart;
    if (e.detail.changeFocus) {
      inputBox.focus();
    }
  }

  _closeKeypadIfTabbedOnAppropriateElement(e) {
    const forwardTabbed = e.keyCode === KEYCODE.TAB && !e.shiftKey;
    const backwardTabbed = e.keyCode === KEYCODE.TAB && e.shiftKey;
    if (forwardTabbed) {
      const footer = this.shadowRoot.querySelector('[name="footer"]');
      if (footer === null) return;
      const footerNodes = footer.assignedNodes({
        flatten: true,
      });
      const hasFooterNode = footerNodes.length !== 0;
      const forwardTabCheckingElement = this._determineForwardTabCheckingElement(
        hasFooterNode,
        footerNodes
      );
      this._closeKeypadIfForwardTabbedOnForwardTabElement(e, forwardTabCheckingElement);
    } else if (backwardTabbed) {
      const functionslot = this.shadowRoot.querySelector('[name="functions"]');
      if (functionslot === null) return;
      const functionNodes = functionslot.assignedNodes({
        flatten: true,
      });
      const hasFunctionNodes = functionNodes.length !== 0;
      const backwardTabCheckingElement = this._determineBackwardTabCheckingElement(
        hasFunctionNodes,
        functionNodes
      );
      this._closeKeypadIfBackwardTabbedOnBackwardTabElement(e, backwardTabCheckingElement);
    } else {
      // invalid condition
    }
  }

  _closeKeypadIfTabbed() {
    this._closeKeypad();
    this._finalCheck();
    this._checkMaxMin();
  }

  _determineForwardTabCheckingElement(hasFooterNode, footerNodes) {
    if (hasFooterNode) {
      return footerNodes[footerNodes.length - 1];
    }
  }

  _determineBackwardTabCheckingElement(hasFunctionNodes, functionNodes) {
    if (hasFunctionNodes) {
      return functionNodes[0];
    }
  }

  _closeKeypadIfForwardTabbedOnForwardTabElement(e, forwardTabCheckingElement) {
    if (e.target === forwardTabCheckingElement) {
      this._closeKeypad();
      this._finalCheck();
      this._checkMaxMin();
    }
  }

  _closeKeypadIfBackwardTabbedOnBackwardTabElement(e, backwardTabCheckingElement) {
    if (e.target === backwardTabCheckingElement) {
      this._closeKeypad();
      this._finalCheck();
      this._checkMaxMin();
    }
  }

  _clearValueIfEnterKeyPressed(e) {
    const outlineOffset = '-0.125rem';
    if (e.keyCode === KEYCODE.ENTER) {
      this._clearField();
    }
    this.style.outlineOffset = outlineOffset;
  }

  _storeCursorPosition(inputBox) {
    this._cursorPosition = inputBox.selectionStart;
  }

  _inputFocus(e) {
    // Open the keypad only if it is closed.
    // Do not try to open keypad if it is already opened.
    // Having this check enables us to avoid unnecessary function calls.

    // Not adding a seperate function here. But calling same code inline here again...
    // so that function calls can be avoided.
    if (!this.keypadActive) {
      this._openKeypadIfAppropriate();
    }
    e.target.onkeydown = (e) => {
      if (!this.inputDisabled) {
        this._addOrSubtractBasedOnKey(e);
      } else {
        e.preventDefault();
      }
    };
  }

  _openKeypadIfAppropriate() {
    if (this.keypad && !this.readonly && !this.disabled) {
      this._openKeypad();
    }
  }

  _openKeypad() {
    this.keypadActive = true;
  }

  _addOrSubtractBasedOnKey(e) {
    switch (e.which) {
      case 38:
        {
          e.preventDefault();
          if (this.disabled || this.readonly) {
            return;
          }
          this._handlePlus();
        }
        break;
      case 40:
        {
          e.preventDefault();
          if (this.disabled || this.readonly) {
            return;
          }
          this._handleMinus();
        }
        break;
      default: {
        // nothing here. Just adding for Sonar.
      }
    }
  }

  _closeKeypadIfBackTabbed(e) {
    if (e.keyCode === KEYCODE.TAB && e.shiftKey) {
      this._finalCheck();
      this._checkMaxMin();
      this._closeKeypad();
    }
  }

  _finalCheck() {
    const isInvalidValue = this.value === '' || isNaN(this.value);
    if (isInvalidValue) {
      this.shadowRoot.querySelector('#input').value = null;
      this.value = '';
      //  else if it is a valid value, then
    } else {
      this.value = Number(parseFloat(this.value)).toString();
      // if it is a decimal number, round it to the number of decimal places.
      if (this.decimals) {
        this.value = Number(this.value).toFixed(Number(this.decimals)).toString();
      }
    }
  }

  _validateOnBlurOnNormalInput() {
    /**
     * Do the validations by executing _finalCheck()
     * only if noValidate is false. This is to provide
     * an option to stop validations if the user wants
     * to be so.
     * From work-item: https://dev.azure.com/shs-ti-ux/SHUI/_workitems/edit/25569/
     */
    if (!this.noValidate) {
      this._finalCheck();
    }
    if (!this.inputDisabled) {
      this._checkMaxMin();
    }
    if (!this.readonly) {
      this.shadowRoot.querySelector('#input').readonly = false;
    }
  }

  _closeInputKeyPad(e) {
    const inputBox = this.shadowRoot.querySelector('#input');
    const hasClickedOutsideKeypad = this._checkIfClickedOutsideKeypad(e);
    if (hasClickedOutsideKeypad) {
      this._validateOnBlurOnKeypadInput(inputBox);
      this.blur();
    }
    // removing blur call from here to avoid issues like cursor disappearing, etc.
    // https://dev.azure.com/shs-ti-ux/SHUI/_workitems/edit/34267
  }

  _validateOnBlurOnKeypadInput(inputBox) {
    this._closeKeypad();
    /**
     * Do the validations by executing _finalCheck()
     * only if noValidate is false. This is to provide
     * an option to stop validations if the user wants
     * to be so.
     * From work-item: https://dev.azure.com/shs-ti-ux/SHUI/_workitems/edit/25569/
     */
    this._checkMaxMin();
    if (!this.readonly) {
      inputBox.readonly = false;
    }
  }

  _closeKeypad() {
    this.keypadActive = false;
  }

  _checkMaxMin() {
    if (this.max !== undefined || this.min !== undefined) {
      if (this.value !== '' && Number(this.value) > Number(this.max)) {
        this.value = this.max;
      }
      if (Number(this.value) < Number(this.min)) {
        this.value = this.min;
      }
    }
  }

  _checkIfClickedOutsideKeypad(e) {
    let clickedOutside;
    const composedPathLength = e.composedPath().length;
    for (let i = 0; i < composedPathLength; i++) {
      const clickedKeypadWrapperOrInputBox =
        e.composedPath()[i] === this.shadowRoot.querySelector('sh-numeric-keypad') ||
        e.composedPath()[i] === this.shadowRoot.querySelector('input') ||
        e.composedPath()[i] === this.shadowRoot.querySelector('#clear') ||
        e.composedPath()[i] === this;
      const clickedFunctionWrapperOrFooterWrapper =
        e.composedPath()[i] === this.shadowRoot.querySelector('.functions-wrapper') ||
        e.composedPath()[i] === this.shadowRoot.querySelector('.footer-wrapper');
      const clickedDivider = e.composedPath()[i] === this.shadowRoot.querySelector('.divider');
      const clickedHost = e.composedPath()[i] === this.shadowRoot.querySelector('.host-wrapper');
      const clickOnControl =
        clickedKeypadWrapperOrInputBox ||
        clickedFunctionWrapperOrFooterWrapper ||
        clickedDivider ||
        clickedHost;

      if (clickOnControl) {
        clickedOutside = false;
        break;
      } else {
        clickedOutside = true;
      }
    }
    return clickedOutside;
  }

  _setBrowserLanguageBasedEuropeanNumberConventionProperty() {
    if (Number('120.94').toLocaleString() === '120,94') {
      this.europeanNumberConvention = true;
    } else if (Number('120.94').toLocaleString() === '120.94') {
      this.europeanNumberConvention = false;
    }
  }

  _handleKeypadActive() {
    if (this.keypadActive) {
      if (this.inputDisabled) {
        document.removeEventListener('keydown', this.keyDownListener);
        this.keyDownListener = this._closeOnInactive.bind(this);
        document.addEventListener('keydown', this.keyDownListener);
      }
      document.body.addEventListener('click', this.closeListener);
      document.body.addEventListener('touchstart', this.closeListener);
      dispatchCustomEvent(this, 'keypad-opened', this);
      clearTimeout(this._rAFFrameControlDelay);
      cancelAnimationFrame(this._positioningFrame);
      this._positioningFrame = window.requestAnimationFrame(this._rAFCallBack);
    } else {
      document.removeEventListener('keydown', this.keyDownListener);
      document.body.removeEventListener('click', this.closeListener);
      document.body.removeEventListener('touchstart', this.closeListener);
      dispatchCustomEvent(this, 'keypad-closed', this);
      clearTimeout(this._rAFFrameControlDelay);
      cancelAnimationFrame(this._positioningFrame);
    }
  }

  _closeOnInactive() {
    if (document.activeElement !== this) {
      this._closeKeypad();
    }
  }

  _showKeypad() {
    this._rAFFrameControlDelay = setTimeout(() => {
      clearTimeout(this._rAFFrameControlDelay);
      this._handleOffset();
      if (this.keypadActive) {
        this._positioningFrame = window.requestAnimationFrame(this._rAFCallBack);
      } else {
        cancelAnimationFrame(this._positioningFrame);
      }
    }, 10);
  }

  _handleOffset() {
    const menu = this.shadowRoot.querySelector('sh-numeric-keypad');
    const baseFontSize = parseFloat(
      window.getComputedStyle(document.querySelector('html'), null).getPropertyValue('font-size')
    );
    // position menu in X axis
    menu.style.left = this.offsetX / 16 + 'rem';
    // extracting the parent element here
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let elem = this;
    let transformPresent;
    let transformedParent;
    /**
     * Check for parent elements up the DOM tree from this dropdown
     * which have css transform property set.
     * If the css transform property is set to something, (translation), then
     * we store that parent element and raise a flag
     *
     * Later we use this flag and this stored parent element to correctly
     * determine the css top value of the internal popover
     */
    while (elem.parentNode && elem.parentNode.nodeName.toLowerCase() !== 'body') {
      if (getComputedStyle(elem.parentNode).transform !== 'none') {
        transformPresent = true;
        transformedParent = elem.parentNode;
        break;
      } else {
        transformPresent = false;
      }
      elem = elem.parentNode;
    }
    this.offsetY = this.getBoundingClientRect().top;

    /**
     * Variables used in this function and their intended meanings:
     *
     * 1. menu              - the popover
     *
     * 2. parentEl          - the parent element as mentioned above
     *
     * 3. this.offsetY      - the dropdown's distance from top of the page
     *                        in pixels got by getBoundingClientRect()
     *
     * 4. menu.clientHeight - the popover's height
     *
     * 5. this.offsetHeight - the dropdown input's height
     *
     * 6. hintHeight        - the height of the hint text
     *
     * 7. transformPresent  - flag to indicate presence of parent with css transform
     *                        property set.
     *
     * 8. transformParent   - the parent element with the css transform property set.
     *
     * If the bottom of the dropdown container which is measured as
     * (popover height (this.offsetHeight) + top of dropdown (this.offsetY) +
     *  dropdown-input height(menu.clientHeight)),
     * is greater than window inner height, then that means the dropdown container
     * goes beyond the window.
     * This means that the dropdown must drop-up.
     *
     * If the transform property is set on the parent...
     * then..
     * popover top must be 0 - the transformed parents top + dropdown's top - popover's height
     * (because top 0rem on the dropdown stays to the top of the transformed parent
     * and not at the top of the page. This is why we have to first set the popover to
     * be at the top of the page by [ 0 - transformed parents top ] which would be like
     * a reference point and then take the dropdown's top, dropdown's input height,
     * popover-height, hint-height, etc to correctly set the top value of the popover according
     * to whether it is drop-up or not).
     *
     * Else, go by the normal calculations.
     */

    /* check if there is not enough space below && enough space ontop */
    var rect = this.getBoundingClientRect();
    if (
      (this.offsetY + menu.clientHeight + this.offsetHeight > window.innerHeight &&
        this.offsetY > menu.clientHeight) ||
      this.isDropUp
    ) {
      menu.style.left = rect.left / baseFontSize + 'rem';
      /* position the keypad ontop */
      if (transformPresent) {
        menu.style.top =
          (0 - transformedParent.getBoundingClientRect().top + this.offsetY - menu.clientHeight) /
            baseFontSize +
          'rem';
      } else {
        menu.style.top = (this.offsetY - menu.clientHeight) / baseFontSize + 'rem';
      }
      /* Adjust keypad position horizontal if position is outside of the window */
      if (rect.left + menu.offsetWidth + 16 > window.innerWidth) {
        let leftOffset = (window.innerWidth - menu.offsetWidth - 16) / baseFontSize + 'rem';
        menu.style.left = leftOffset;
      } else {
        if (menu.style.left) {
          menu.style.left = rect.left / baseFontSize + 'rem';
        }
      }
      /* check if enough space below */
    } else if (this.offsetY + menu.clientHeight + this.offsetHeight < window.innerHeight) {
      menu.style.left = rect.left / baseFontSize + 'rem';
      /* position the keypad below */
      if (transformPresent) {
        menu.style.top =
          (0 - transformedParent.getBoundingClientRect().top + this.offsetY + this.offsetHeight) /
            baseFontSize +
          'rem';
      } else {
        menu.style.top = (this.offsetY + this.offsetHeight) / baseFontSize + 'rem';
      }
      /* Adjust keypad position horizontal if position is outside of the window */

      if (rect.left + menu.offsetWidth + 16 > window.innerWidth) {
        let leftOffset = (window.innerWidth - menu.offsetWidth - 16) / baseFontSize + 'rem';
        menu.style.left = leftOffset;
      } else {
        if (menu.style.left) {
          menu.style.left = rect.left / baseFontSize + 'rem';
        }
      }
    } else {
      var rect = this.getBoundingClientRect();
      /* check if left is enough space */
      if (rect.left > menu.clientWidth) {
        /* position the keypad left */
        menu.style.left = (rect.left - menu.clientWidth) / baseFontSize + 'rem';
        menu.style.top = (window.innerHeight - menu.clientHeight - 16) / baseFontSize + 'rem';
      } else {
        /* position keypad right */
        if (rect.left + this.offsetWidth + menu.offsetWidth + 16 < window.innerWidth) {
          let leftOffset = (rect.left + this.offsetWidth) / baseFontSize + 'rem';
          menu.style.left = leftOffset;
          menu.style.top = (window.innerHeight - menu.clientHeight - 16) / baseFontSize + 'rem';
        } else {
          /* if not enough space on the right keep keypad in the view */
          let leftOffset = (window.innerWidth - menu.offsetWidth - 16) / baseFontSize + 'rem';
          menu.style.left = leftOffset;
        }
      }
    }
  }

  _keypadObserver() {
    if (this.keypad) {
      this._inputMode = 'none';
    } else {
      this._inputMode = 'numeric';
    }
  }

  _handleInputDisabledChange() {
    document.removeEventListener('keydown', this.keyDownListener);
    if (this.inputDisabled && this.keypadActive) {
      this.keyDownListener = this._closeOnInactive.bind(this);
      document.addEventListener('keydown', this.keyDownListener);
    }
  }

  _decimalsObserver() {
    const decimalsInNumber = Number(this.decimals);
    /* this below assignment to step of input is to prevent HTML from showing warning
       tooltip */
    this.shadowRoot.querySelector('#input').step = Math.pow(10, -decimalsInNumber);
  }

  _displayValueChange() {
    const newValueIsNotEmpty = this._displayValue ? true : false;
    if (this._displayValue === '') {
      this.shadowRoot.querySelector('#input').value = this._displayValue;
    }
    if (newValueIsNotEmpty) {
      this.shadowRoot.querySelector('#input').value = this._displayValue;
    }
  }

  _restrictOnlyNumberKeys(e) {
    const isValidInput = this._checkIfValidInput(e);
    if (isValidInput && !this.inputDisabled) {
      let numberSystem;
      if (this.europeanNumberConvention === undefined || this.europeanNumberConvention === null) {
        numberSystem = decimalSeparator === ',' ? 'European' : 'English';
      } else {
        numberSystem = this.europeanNumberConvention ? 'European' : 'English';
      }
      // preventing commas and dots in respective number conventions inorder to force allow
      // only one separator to be present at a time.
      if (numberSystem === 'English' && e.key === ',') {
        e.preventDefault();
      }
      if (numberSystem === 'European' && e.key === '.') {
        e.preventDefault();
      }
    } else {
      e.preventDefault();
    }
  }

  _checkIfValidInput(e) {
    let isValidInput = false;
    const eventKey = e.key;
    const allowedKeys = [
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      'e',
      'Shift',
      'Enter',
      'Tab',
      'Backspace',
      'Delete',
      'Home',
      'End',
      'ArrowUp',
      'ArrowDown',
      'ArrowRight',
      'ArrowLeft',
      '-',
      '+',
      ',',
      '=',
      '.',
      'Space',
    ];
    const numberOfAllowedKeys = allowedKeys.length;
    for (let i = 0; i < numberOfAllowedKeys; i++) {
      if (eventKey === allowedKeys[i]) {
        isValidInput = true;
        break;
      } else if (
        (e.metaKey || e.ctrlKey) &&
        [KEYCODE.V, KEYCODE.C, KEYCODE.A, KEYCODE.X, KEYCODE.Z, KEYCODE.Y].indexOf(e.keyCode) != -1
      ) {
        isValidInput = true;
        break;
      }
    }
    return isValidInput;
  }

  _updateDisplayValue(e) {
    this.value = e.target.value.toString().split(',').join('.');
  }

  _clearField() {
    this.value = '';
    this.shadowRoot.querySelector('#input').focus();
  }

  _startMinusCounter(e) {
    if (e.touches) {
      this.skipMouseMove = true;
    }
    e.target.style.transition = '0s all linear';
    this._releaseMinusCounter();
    this._releasePlusCounter();
    this.minusClock = setInterval(this._handleMinusOnMouseDown, 100, this);
  }

  _startPlusCounter(e) {
    if (e.touches) {
      this.skipMouseMove = true;
    }
    e.target.style.transition = '0s all linear';
    this._releasePlusCounter();
    this._releaseMinusCounter();
    this.plusClock = setInterval(this._handlePlusOnMouseDown, 100, this);
  }

  _releasePlusCounter(e = null) {
    // the below check for mousemove was to prevent mousemove from clearing the timer for repeated addition/subtraction in touch scenarios
    // in some touch scenarios, the mousemove was coming after touchstart and before touchend. This led to clearing of timer
    // before release of touch thus leading to stopping of repeated addition after a few seconds, even if the element was still
    // under touch. (The releasePlusCounter/releaseMinusCounter was called by the mousemove event handler attached to the document body)
    // So to prevent this scenario we set a flag when we detect mousemove.
    // If touch is also detected and this flag is also set, then that means we got an erroneous condition as mentioned above.
    // In that case, we reset the flag and return to prevent further execution.
    // Else we proceed to do the addition/subtraction

    // To prevent additional complexities, it is better to handle click event actions in
    // mouseup/touchend itself instead of adding a seperate click handler (applicable for cases where an explicit mousup/ touchend event handler
    // is added)

    // Also event.preventDefault() has to be called after touchend actions are done, to prevent the subsequent mouse event handlers from executing.
    if (e && e.type === 'keypress' && e.keyCode !== KEYCODE.ENTER) {
      return;
    }
    if (e && e.type === 'mousemove' && this.skipMouseMove) {
      this.skipMouseMove = false;
      return;
    }
    if (this.plusIcon) {
      clearInterval(this.plusClock);
    }
    if (this.minusIcon) {
      clearInterval(this.minusClock);
    }
    clearInterval(this.plusClock);
    if (e && e.type !== 'mousemove') {
      this._handlePlus();
    }
    if (e && e.touches) {
      e.preventDefault();
    }
  }

  _releaseMinusCounter(e = null) {
    // the below check for mousemove was to prevent mousemove from clearing the timer for repeated addition/subtraction in touch scenarios
    // in some touch scenarios, the mousemove was coming after touchstart and before touchend. This led to clearing of timer
    // before release of touch thus leading to stopping of repeated addition after a few seconds, even if the element was still
    // under touch. (The releasePlusCounter/releaseMinusCounter was called by the mousemove event handler attached to the document body)
    // So to prevent this scenario we set a flag when we detect mousemove.
    // If touch is also detected and this flag is also set, then that means we got an erroneous condition as mentioned above.
    // In that case, we reset the flag and return to prevent further execution.
    // Else we proceed to do the addition/subtraction

    // To prevent additional complexities, it is better to handle click event actions in
    // mouseup/touchend itself instead of adding a seperate click handler (applicable for cases where an explicit mousup/ touchend event handler
    // is added)

    // Also event.preventDefault() has to be called after touchend actions are done, to prevent the subsequent mouse event handlers from executing.
    if (e && e.type === 'keypress' && e.keyCode !== KEYCODE.ENTER) {
      return;
    }
    if (e && e.type === 'mousemove' && this.skipMouseMove) {
      this.skipMouseMove = false;
      return;
    }
    if (this.plusIcon) {
      clearInterval(this.plusClock);
    }
    if (this.minusIcon) {
      clearInterval(this.minusClock);
    }
    clearInterval(this.minusClock);
    if (e && e.type !== 'mousemove') {
      this._handleMinus();
    }
    if (e && e.touches) {
      e.preventDefault();
    }
  }

  _handlePlusOnMouseDown(self) {
    /**
     * This function is triggered when the right facing icon
     * is pressed. This function adds the value to step after
     * necessary calculations and emits an event 'plus pressed'
     *
     * 1. If value is null or undefined, it is set to ''.
     * 2. If step is null or undefined, it is set to '1'.
     * 3. Get required number of decimal places in the value.
     * 4. Convert decimal separators in step and value to dots.
     * 5. Add the numerical values of step and value.
     * 6. Replace all dots in the value and step
     *    back to decimal separators.
     * 7. Check whether value is within max and min values.
     * 8. Either save this value as stable value (if this value is
     *    a valid value) or renew the stable value as the required
     *    value.
     */
    if (!self.disabled) {
      if (self.value === null || self.value === undefined) {
        self.value = '';
      }
      if (self.step === null || self.step === undefined) {
        self.step = '1';
      }
      const finalNumberOfDecimalPoints = self._getNumberOfPrecision();
      self.value = Number(self.step) + Number(self.value);
      self.value = Number(self.value).toFixed(finalNumberOfDecimalPoints);
      self._checkMaxMin();
      self._renewStableValue(self.value);
      self.minusIcon = false;
      self.plusIcon = true;
      // fire additional custom event
      dispatchCustomEvent(self, 'plusPressed', self);
      /* mouseDownAddition is a flag to indicate that mouse down addition has happened. This flag
         is checked inside the handlePlus block to prevent subsequent addition due to click  */
      self.mouseDownAddition = true;
    } else {
      self._releasePlusCounter();
    }
  }

  _getNumberOfPrecision() {
    /**
     *  Function to get the required number of decimal
     *  places in the result, by either taking the maximum
     *  number of decimal places in the step and value, or
     *  by taking the value specified in the decimals property
     *  (if specified).
     *
     * 1. Replace all decimal separators with dots of both
     *    step and copy of value.
     * 2. Extracts the number of decimal places in step &
     *    value.
     * 3. Compares them and the maximum number is extracted
     *    and placed as the required number of decimal places
     *    in the result.
     * 4. If the component already has the property 'decimals',
     *    then, that property is set as the required number of
     *    the decimal places in the result regardless of the
     *    above calculation.
     * 5. If not, the number of decimal places calculated will
     *    be set as the required number of decimal places.
     */
    const valueInString = this.value.toString();
    let valueHasDecimalPoints = false;
    let stepHasDecimalPoints = false;
    let numberOfDecimalPointsOfValue = 0;
    let numberOfDecimalPointsOfStep = 0;
    let finalNumberOfDecimalPoints = 0;
    if (this.step !== Math.round(this.step)) {
      stepHasDecimalPoints = true;
      numberOfDecimalPointsOfStep = ('' + this.step).split('.')[1].length;
    }
    if (valueInString.includes('.')) {
      valueHasDecimalPoints = true;
      numberOfDecimalPointsOfValue = valueInString.split('.')[1].length;
    }
    if (valueHasDecimalPoints || stepHasDecimalPoints) {
      if (numberOfDecimalPointsOfStep > numberOfDecimalPointsOfValue) {
        finalNumberOfDecimalPoints = numberOfDecimalPointsOfStep;
      } else {
        finalNumberOfDecimalPoints = numberOfDecimalPointsOfValue;
      }
    }
    const decimalPoints = this.decimals;
    const numOfDecimalPoints = Number(decimalPoints);
    if (decimalPoints.length !== 0 && numOfDecimalPoints > 0) {
      this.shadowRoot.querySelector('#input').step = Math.pow(10, -numOfDecimalPoints);
      return numOfDecimalPoints;
    } else {
      this.shadowRoot.querySelector('#input').step = Math.pow(10, -finalNumberOfDecimalPoints);
      return finalNumberOfDecimalPoints;
    }
  }

  _handleMinusOnMouseDown(self) {
    /**
     * This function is triggered when the left facing icon
     * is pressed. This function subtracts the step from value
     * after necessary calculations and emits an event 'plus pressed'.
     *
     * 1. If value is null or undefined, it is set to '0'.
     * 2. If step is null or undefined, it is set to '1'.
     * 3. Get required number of decimal places in the value.
     * 4. Convert decimal separators in step and value to dots.
     * 5. Add the numerical values of step and value.
     * 6. Replace all dots in the value and step back to decimal
     *    separators.
     * 7. Check whether value is within max and min values.
     * 8. Either save this value as stable value (if this value is
     *    a valid value) or renew the stable value as the required
     *    value.
     */
    if (!self.disabled) {
      if (self.value === null || self.value === undefined) {
        self.value = '';
      }
      if (self.step === null || self.step === undefined) {
        self.step = '1';
      }
      const finalNumberOfDecimalPoints = self._getNumberOfPrecision();
      self.value = Number(self.value) - Number(self.step);
      self.value = Number(self.value).toFixed(finalNumberOfDecimalPoints);
      self._checkMaxMin();
      self._renewStableValue(self.value);
      self.plusIcon = false;
      self.minusIcon = true;
      // fire additional custom event
      dispatchCustomEvent(self, 'minusPressed', self);
      /* mouseDownSubtraction is a flag to indicate that mouse down subtraction has happened. This flag
         is checked inside the handleMinus block to prevent subsequent subtraction due to click  */
      self.mouseDownSubtraction = true;
    } else {
      self._releaseMinusCounter();
    }
  }

  _handlePlus() {
    /**
     * This function is triggered when the right facing icon
     * is pressed. This function adds the value to step after
     * necessary calculations and emits an event 'plus pressed'
     *
     * 1. If value is null or undefined, it is set to ''.
     * 2. If step is null or undefined, it is set to '1'.
     * 3. Get required number of decimal places in the value.
     * 4. Convert decimal separators in step and value to dots.
     * 5. Add the numerical values of step and value.
     * 6. Replace all dots in the value and step
     *    back to decimal separators.
     * 7. Check whether value is within max and min values.
     * 8. Either save this value as stable value (if this value is
     *    a valid value) or renew the stable value as the required
     *    value.
     */
    /* the below if-check is to prevent double addition due to both mousedown and click
       firing simultaneously.  */
    if (!this.disabled) {
      if (this.mouseDownAddition) {
        this.mouseDownAddition = false;
        return;
      }
      if (this.value === null || this.value === undefined) {
        this.value = '';
      }
      if (this.step === null || this.step === undefined) {
        this.step = '1';
      }
      const finalNumberOfDecimalPoints = this._getNumberOfPrecision();
      this.value = Number(this.step) + Number(this.value);
      this.value = Number(this.value).toFixed(finalNumberOfDecimalPoints);
      this._checkMaxMin();
      this._renewStableValue(this.value);

      // fire additional custom event
      /** @ignore */
      dispatchCustomEvent(this, 'plusPressed', this);
    }
    this._releaseMinusCounter();
    this._releasePlusCounter();
  }

  _handleMinus() {
    /**
     * This function is triggered when the left facing icon
     * is pressed. This function subtracts the step from value
     * after necessary calculations and emits an event 'plus pressed'.
     *
     * 1. If value is null or undefined, it is set to '0'.
     * 2. If step is null or undefined, it is set to '1'.
     * 3. Get required number of decimal places in the value.
     * 4. Convert decimal separators in step and value to dots.
     * 5. Add the numerical values of step and value.
     * 6. Replace all dots in the value and step back to decimal
     *    separators.
     * 7. Check whether value is within max and min values.
     * 8. Either save this value as stable value (if this value is
     *    a valid value) or renew the stable value as the required
     *    value.
     */
    /* the below if-check is to prevent double subtraction due to both mousedown and click
       firing simultaneously.  */
    if (!this.disabled) {
      if (this.mouseDownSubtraction) {
        this.mouseDownSubtraction = false;
        return;
      }
      if (this.value === null || this.value === undefined) {
        this.value = '';
      }
      if (this.step === null || this.step === undefined) {
        this.step = '1';
      }
      const finalNumberOfDecimalPoints = this._getNumberOfPrecision();
      this.value = Number(this.value) - Number(this.step);
      this.value = Number(this.value).toFixed(finalNumberOfDecimalPoints);
      this._checkMaxMin();
      this._renewStableValue(this.value);

      // fire additional custom event
      /** @ignore */
      dispatchCustomEvent(this, 'minusPressed', this);
    }
    this._releaseMinusCounter();
    this._releasePlusCounter();
  }

  _renewStableValue(value) {
    let tempValue = Number(value);
    /**
     *  Below if-check is to save the value as a stable value.
     *  If the host contains min and max properties, then save the
     *  current value as stable value only if it satisfies :
     *  Number(value) >= Number(min) and Number(value) <= Number(max)
     *
     *  If the host does not contain min and max properties,
     *  then the current value is by default a stable value.
     **/
    if (this.min && this.max) {
      if (Number(this.value) >= Number(this.min) && Number(this.value) <= Number(this.max)) {
        this.stableValue = tempValue;
      }
    } else {
      this.stableValue = tempValue;
    }
  }

  _europeanNumberConventionObserver() {
    const replaceSeparator = this.europeanNumberConvention ? '.' : ',';
    this._decimalSeparator = this.europeanNumberConvention ? ',' : '.';
    if (this._displayValue) {
      this._displayValue = this._displayValue
        .toString()
        .split(replaceSeparator)
        .join(this._decimalSeparator);
      this._displayValueChange();
    }
  }

  disconnectedCallback() {
    if (this._rAFFrameControlDelay) {
      clearTimeout(this._rAFFrameControlDelay);
    }
    if (this._positioningFrame) {
      cancelAnimationFrame(this._positioningFrame);
    }
    if (this.keyDownListener) {
      document.removeEventListener('keydown', this.keyDownListener);
    }
    document.body.removeEventListener('click', this.closeListener);
    document.body.removeEventListener('mousemove', this.boundMove);
    document.body.removeEventListener('click', this.closeListener);
    super.disconnectedCallback();
  }
}
customElements.define('sh-input-number', SHInputNumber);
